import { Component, Inject, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialogRef as MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { offsetTypes } from '../../../../../../../../../../shared/core/shared.selectors';
import { takeUntil } from 'rxjs/operators';
import { DailyReportService } from '../../../../daily-report.service';
import { projectDetails } from '../../../../../../../../core/projects.selectors';

@Component({
    selector: 'app-activity-codes-station-range',
    templateUrl: './activity-codes-station-range.component.html',
    styleUrls: ['./activity-codes-station-range.component.scss'],
    standalone: false
})
export class ActivityCodesStationRangeComponent implements OnInit, OnChanges, OnDestroy {
  fields: any = [];

  form: FormGroup = new FormGroup({});
  offsetTypeOptions: any = [];
  alignmentOptions: any[] = [];
  stationOptions: any[] = [];
  filteredFromStationOptions: any[] = [];
  filteredToStationOptions: any[] = [];
  stationRangeMapData: any = null;
  hasAlignmentGeometry = false;
  private alignments: any[] = [];
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private dailyReportService: DailyReportService,
    public dialogRef: MatDialogRef<ActivityCodesStationRangeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    if (data) {
      this.form = data.form;
    }
  }

  ngOnInit() {
    this.store.select(offsetTypes)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.offsetTypeOptions = data;
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.alignments = this.parseAlignmentGeometry(project?.alignment_geometry || project?.alignmentGeometry);
        this.hasAlignmentGeometry = this.alignments.length > 0;
        this.updateDialogSize();
        this.alignmentOptions = this.alignments.map(alignment => ({
          value: alignment.name,
          label: alignment.name,
        }));
        if (!this.form.controls?.alignment?.value && this.alignmentOptions.length) {
          this.form.controls.alignment?.setValue(this.alignmentOptions[0].value, { emitEvent: false });
        }
        this.updateStationOptions();
        this.updateFilteredStationOptions();
        this.updateStationRangeMapData();
      });

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.updateStationOptions();
        this.updateFilteredStationOptions();
        this.updateStationRangeMapData();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.checkStationFormData();
  }

  private updateDialogSize() {
    this.dialogRef.updateSize(
      this.hasAlignmentGeometry ? '1200px' : '552px',
      this.hasAlignmentGeometry ? '620px' : 'auto',
    );
  }

  save() {
    this.dialogRef.close();

    const {
      stationRangeFromStation,
      stationRangeFromOffsetType,
      stationRangeFromOffsetDistance,
      stationRangeToStation,
      stationRangeToOffsetType,
      stationRangeToOffsetDistance,
      alignment,
    } = this.form.getRawValue();

    const stationRange = {
      from: {
        station: stationRangeFromStation,
        offsetType: stationRangeFromOffsetType,
        offsetDistance: stationRangeFromOffsetDistance,
      },
      to: {
        station: stationRangeToStation,
        offsetType: stationRangeToOffsetType,
        offsetDistance: stationRangeToOffsetDistance,
      },
    };

    this.dailyReportService.setStationRange.next({ stationRange, alignment });
  }


  checkStationFormData() {
    const stationRangeFromStationValue = this.form.controls?.['stationRangeFromStation'].value;
    const stationRangeFromOffsetTypeValue = this.form.controls?.['stationRangeFromOffsetType'].value;
    const stationRangeFromOffsetDistanceValue = this.form.controls?.['stationRangeFromOffsetDistance'].value;
    const stationRangeToStationValue = this.form.controls?.['stationRangeToStation'].value;
    const stationRangeToOffsetTypeValue = this.form.controls?.['stationRangeToOffsetType'].value;
    const stationRangeToOffsetDistanceValue = this.form.controls?.['stationRangeToOffsetDistance'].value;
    const alignmentValue = this.form.controls?.['alignment'].value;

    return !!(
      stationRangeFromStationValue
      || stationRangeFromOffsetTypeValue
      || stationRangeFromOffsetDistanceValue
      || stationRangeToStationValue
      || stationRangeToOffsetTypeValue
      || stationRangeToOffsetDistanceValue
      || alignmentValue
    );
  }

  close() {
    this.dialogRef.close();
  }

  private updateStationOptions() {
    const alignment = this.getSelectedAlignment();
    const options = (alignment?.stations || []).map(station => ({
      value: station.name,
      label: station.name,
    }));
    [
      this.form.controls?.stationRangeFromStation?.value,
      this.form.controls?.stationRangeToStation?.value,
    ].filter(Boolean).forEach(stationName => {
      if (!options.some(option => option.value === stationName)) {
        options.push({ value: stationName, label: stationName });
      }
    });
    this.stationOptions = options;
  }

  private updateFilteredStationOptions() {
    this.filteredFromStationOptions = this.getFilteredStationOptions('stationRangeFromStation');
    this.filteredToStationOptions = this.getFilteredStationOptions('stationRangeToStation');
  }

  private getFilteredStationOptions(controlName: string) {
    const value = `${this.form.controls?.[controlName]?.value || ''}`;
    const search = value.toLowerCase();
    const matches = search
      ? this.stationOptions.filter(option => `${option.label}`.toLowerCase().includes(search))
      : this.stationOptions;
    const limitedMatches = matches.slice(0, 25);
    if (value && !limitedMatches.some(option => option.value === value)) {
      return [{ value, label: value }, ...limitedMatches];
    }
    return limitedMatches;
  }

  private getSelectedAlignment() {
    const alignmentName = this.form.controls?.alignment?.value;
    return alignmentName
      ? this.alignments.find(alignment => alignment.name === alignmentName)
      : this.alignments[0];
  }

  private updateStationRangeMapData() {
    const alignment = this.getSelectedAlignment();
    if (!alignment?.polylineCoordinates?.length) {
      this.stationRangeMapData = null;
      return;
    }

    const fromStationName = this.form.controls?.stationRangeFromStation?.value;
    const toStationName = this.form.controls?.stationRangeToStation?.value;
    const fromOffsetType = this.form.controls?.stationRangeFromOffsetType?.value;
    const fromOffsetDistance = this.form.controls?.stationRangeFromOffsetDistance?.value;
    const toOffsetType = this.form.controls?.stationRangeToOffsetType?.value;
    const toOffsetDistance = this.form.controls?.stationRangeToOffsetDistance?.value;
    const selectedPath = this.getStationRangePolyline({ alignment, fromStationName, toStationName });
    const path = selectedPath.length >= 2 ? selectedPath : alignment.polylineCoordinates;

    const fromOffsetPoint = this.hasNonZeroOffset(fromOffsetType, fromOffsetDistance) && selectedPath.length >= 2
      ? this.getOffsetCoordinate({
        coordinate: selectedPath[0],
        previousCoordinate: selectedPath[0],
        nextCoordinate: selectedPath[1],
        offsetFeet: this.getSignedOffsetFeet({ offsetType: fromOffsetType, offsetDistance: fromOffsetDistance }),
      })
      : null;
    const toOffsetPoint = this.hasNonZeroOffset(toOffsetType, toOffsetDistance) && selectedPath.length >= 2
      ? this.getOffsetCoordinate({
        coordinate: selectedPath[selectedPath.length - 1],
        previousCoordinate: selectedPath[selectedPath.length - 2],
        nextCoordinate: selectedPath[selectedPath.length - 1],
        offsetFeet: this.getSignedOffsetFeet({ offsetType: toOffsetType, offsetDistance: toOffsetDistance }),
      })
      : null;

    this.stationRangeMapData = {
      center: this.getMapCenter(path),
      geometry: this.buildLineStringFeatureCollection(alignment.polylineCoordinates, selectedPath, fromOffsetPoint, toOffsetPoint),
      selectedPath: path,
      endCapPaths: selectedPath.length >= 2 ? this.getStationRangeEndCapPaths(path[0], path[path.length - 1]) : [],
      fromPosition: selectedPath.length >= 2 ? path[0] : null,
      toPosition: selectedPath.length >= 2 ? path[path.length - 1] : null,
      fromOffsetPoint,
      toOffsetPoint,
      fromLabel: selectedPath.length >= 2 ? fromStationName : null,
      toLabel: selectedPath.length >= 2 ? toStationName : null,
      zoom: this.getStationRangeMapZoom(path),
    };
  }

  private hasNonZeroOffset(offsetType: any, offsetDistance: any) {
    const type = `${offsetType || ''}`.toUpperCase();
    return (type.startsWith('RT') || type.startsWith('LT')) && Number(offsetDistance) > 0;
  }

  private parseStationToFeet(stationName: string): number | null {
    const match = `${stationName || ''}`.match(/^(\d+)\+(\d+)$/);
    if (!match) return null;
    return parseInt(match[1], 10) * 100 + parseInt(match[2], 10);
  }

  private getOffsetDirectionMultiplier(offsetType: string) {
    if (`${offsetType || ''}`.startsWith('RT')) return -1;
    if (`${offsetType || ''}`.startsWith('LT')) return 1;
    return 0;
  }

  private getSignedOffsetFeet({ offsetType, offsetDistance }) {
    const distance = parseFloat(offsetDistance);
    return this.getOffsetDirectionMultiplier(offsetType) * (Number.isFinite(distance) ? distance : 0);
  }

  private getOffsetCoordinate({ coordinate, previousCoordinate, nextCoordinate, offsetFeet }) {
    if (!coordinate || !offsetFeet) return coordinate;
    const tangentStart = previousCoordinate || coordinate;
    const tangentEnd = nextCoordinate || coordinate;
    const earthRadiusFeet = 20902231;
    const toRad = deg => (deg * Math.PI) / 180;
    const toDeg = rad => (rad * 180) / Math.PI;
    const cosLat = Math.max(Math.cos(toRad(coordinate.lat)), 0.000001);
    const dx = toRad(tangentEnd.lng - tangentStart.lng) * earthRadiusFeet * cosLat;
    const dy = toRad(tangentEnd.lat - tangentStart.lat) * earthRadiusFeet;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (!length) return coordinate;

    const leftNormalX = -dy / length;
    const leftNormalY = dx / length;
    const offsetX = leftNormalX * offsetFeet;
    const offsetY = leftNormalY * offsetFeet;

    return {
      lat: coordinate.lat + toDeg(offsetY / earthRadiusFeet),
      lng: coordinate.lng + toDeg(offsetX / (earthRadiusFeet * cosLat)),
    };
  }

  private makeCoordinate(coordinate: any) {
    if (!coordinate) return null;
    if (Array.isArray(coordinate) && coordinate.length >= 2) {
      return { lat: Number(coordinate[1]), lng: Number(coordinate[0]) };
    }
    const lat = Number(coordinate.lat ?? coordinate.latitude);
    const lng = Number(coordinate.lng ?? coordinate.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
    return null;
  }

  private parseAlignmentGeometry(alignmentGeometry: any) {
    try {
      const geojson = typeof alignmentGeometry === 'string'
        ? JSON.parse(alignmentGeometry)
        : alignmentGeometry;
      if (!geojson?.features?.length) return [];

      return geojson.features.map(feature => {
        const coordinates = feature?.geometry?.coordinates || [];
        const rawStations = feature?.properties?.stations || [];
        const stations = rawStations.map((station, index) => {
          const coordinate = this.makeCoordinate(coordinates[index]);
          return coordinate ? { name: station?.name, coordinate } : null;
        }).filter(Boolean);

        return {
          name: feature?.properties?.name || '',
          polylineCoordinates: coordinates.map(coordinate => this.makeCoordinate(coordinate)).filter(Boolean),
          stations,
        };
      });
    } catch {
      return [];
    }
  }

  private getStationRangePolyline({ alignment, fromStationName, toStationName }) {
    const fromFeet = this.parseStationToFeet(fromStationName);
    const toFeet = this.parseStationToFeet(toStationName);
    const stations = alignment?.stations || [];

    if (fromFeet == null || toFeet == null || stations.length < 2 || !alignment?.polylineCoordinates?.length) {
      return [];
    }

    const rangeStart = Math.min(fromFeet, toFeet);
    const rangeEnd = Math.max(fromFeet, toFeet);
    const points = [];

    for (let index = 0; index < stations.length - 1; index++) {
      const segmentStartFeet = this.parseStationToFeet(stations[index].name);
      const segmentEndFeet = this.parseStationToFeet(stations[index + 1].name);
      const segmentStart = stations[index].coordinate;
      const segmentEnd = stations[index + 1].coordinate;

      if (segmentStartFeet == null || segmentEndFeet == null || !segmentStart || !segmentEnd) {
        continue;
      }

      const segmentLow = Math.min(segmentStartFeet, segmentEndFeet);
      const segmentHigh = Math.max(segmentStartFeet, segmentEndFeet);
      const overlapStart = Math.max(rangeStart, segmentLow);
      const overlapEnd = Math.min(rangeEnd, segmentHigh);

      if (overlapStart > overlapEnd) {
        continue;
      }

      const coordinateForFeet = feet => {
        const ratio = segmentEndFeet === segmentStartFeet
          ? 0
          : (feet - segmentStartFeet) / (segmentEndFeet - segmentStartFeet);
        return {
          lat: segmentStart.lat + ratio * (segmentEnd.lat - segmentStart.lat),
          lng: segmentStart.lng + ratio * (segmentEnd.lng - segmentStart.lng),
        };
      };

      const startCoordinate = coordinateForFeet(overlapStart);
      const endCoordinate = coordinateForFeet(overlapEnd);
      const lastPoint = points[points.length - 1];

      if (!lastPoint || lastPoint.lat !== startCoordinate.lat || lastPoint.lng !== startCoordinate.lng) {
        points.push(startCoordinate);
      }
      points.push(endCoordinate);
    }

    return points;
  }

  private getMapCenter(points: any[]) {
    const totals = points.reduce((acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng,
    }), { lat: 0, lng: 0 });

    return {
      lat: totals.lat / points.length,
      lng: totals.lng / points.length,
    };
  }

  private getStationRangeMapZoom(points: any[]) {
    const bounds = points.reduce((acc, point) => ({
      minLat: Math.min(acc.minLat, point.lat),
      maxLat: Math.max(acc.maxLat, point.lat),
      minLng: Math.min(acc.minLng, point.lng),
      maxLng: Math.max(acc.maxLng, point.lng),
    }), {
      minLat: points[0].lat,
      maxLat: points[0].lat,
      minLng: points[0].lng,
      maxLng: points[0].lng,
    });
    const span = Math.max(bounds.maxLat - bounds.minLat, bounds.maxLng - bounds.minLng);

    if (span < 0.0003) return 19;
    if (span < 0.0008) return 18;
    return 17;
  }

  private getStationRangeEndCapPaths(start: any, end: any) {
    if (!start || !end) return [];

    const latDelta = end.lat - start.lat;
    const lngDelta = end.lng - start.lng;
    const length = Math.sqrt(latDelta * latDelta + lngDelta * lngDelta);
    if (!length) return [];

    const perpendicularLat = -lngDelta / length;
    const perpendicularLng = latDelta / length;
    const capLength = Math.max(length * 0.16, 0.000055);
    const halfCapLength = capLength / 2;

    const capForPoint = point => [
      {
        lat: point.lat - perpendicularLat * halfCapLength,
        lng: point.lng - perpendicularLng * halfCapLength,
      },
      {
        lat: point.lat + perpendicularLat * halfCapLength,
        lng: point.lng + perpendicularLng * halfCapLength,
      },
    ];

    return [capForPoint(start), capForPoint(end)];
  }

  private buildLineStringFeatureCollection(alignmentPath: any[], selectedPath: any[] = [], fromOffsetPoint: any = null, toOffsetPoint: any = null) {
    const features = [];
    if (alignmentPath?.length >= 2) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: alignmentPath.map(point => [point.lng, point.lat]),
          color: selectedPath?.length >= 2 ? '#8EA0D8' : '#1036D6',
          opacity: selectedPath?.length >= 2 ? 0.55 : 1,
          strokeWidth: selectedPath?.length >= 2 ? 2 : 3,
          includeInExtent: selectedPath?.length < 2,
        },
      });
    }

    if (selectedPath?.length >= 2) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: selectedPath.map(point => [point.lng, point.lat]),
          color: '#1036D6',
          lineStyle: 'short-dash',
          strokeWidth: 4,
        },
      });

      this.getStationRangeEndCapPaths(selectedPath[0], selectedPath[selectedPath.length - 1]).forEach(capPath => {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: capPath.map(point => [point.lng, point.lat]),
            color: '#1036D6',
            strokeWidth: 4,
          },
        });
      });

      [
        { endpoint: selectedPath[0], offset: fromOffsetPoint },
        { endpoint: selectedPath[selectedPath.length - 1], offset: toOffsetPoint },
      ].forEach(({ endpoint, offset }) => {
        if (!offset) return;
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [[endpoint.lng, endpoint.lat], [offset.lng, offset.lat]],
            color: '#1036D6',
            strokeWidth: 1.5,
          },
        });
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: [offset.lng, offset.lat],
            color: '#1036D6',
            markerIconUrl: '/assets/icons/map/markers/offset-ring.svg',
            iconSize: { width: 10, height: 10, yOffset: 5 },
            includeInExtent: true,
          },
        });
      });
    }

    return {
      type: 'FeatureCollection',
      features,
    };
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
